"use client";

import { message, Modal, notification } from 'antd';
import React, { useEffect, useRef } from 'react';
import { setGlobalModal } from '@/lib/global-modal';

/**
 * 全局 App Provider
 * 初始化全局 message、modal、notification 实例
 * 简化版：主要用于网络错误弹窗等全局功能
 */
export function GlobalAppProvider({ children }: { children: React.ReactNode }) {
  // 使用 message.useMessage() 和 Modal.useModal() 获取带 context 的实例
  // 这种方式不依赖于外层 <App> 组件的包裹，可以独立工作
  const [messageApi, messageContextHolder] = message.useMessage();
  const [modalApi, modalContextHolder] = Modal.useModal();
  const [notificationApi, notificationContextHolder] = notification.useNotification();

  // 使用 ref 追踪是否已初始化，避免重复设置
  const initializedRef = useRef(false);

  useEffect(() => {
    // 避免重复初始化
    if (initializedRef.current) return;
    initializedRef.current = true;

    if (typeof window !== 'undefined') {
      (window as any).$message = messageApi;
      (window as any).$modal = modalApi;
      (window as any).$notification = notificationApi;
    }
    setGlobalModal(modalApi, null);
  }, [messageApi, modalApi, notificationApi]);

  return (
    <>
      {messageContextHolder}
      {modalContextHolder}
      {notificationContextHolder}
      {children}
    </>
  );
}

/**
 * 获取全局 message 实例
 * 兜底使用静态方法
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getGlobalMessage(): any {
  if (typeof window !== 'undefined' && (window as any).$message) {
    return (window as any).$message;
  }
  // 确保总是返回有效的 message 实例
  return message;
}

/**
 * 获取全局 modal 实例
 * 兜底使用静态方法
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getGlobalModal(): any {
  if (typeof window !== 'undefined' && (window as any).$modal) {
    return (window as any).$modal;
  }
  // 确保总是返回有效的 modal 实例
  return Modal;
}

/**
 * 获取全局 notification 实例
 * 兜底使用静态方法
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getGlobalNotification(): any {
  if (typeof window !== 'undefined' && (window as any).$notification) {
    return (window as any).$notification;
  }
  // 确保总是返回有效的 notification 实例
  return notification;
}

